// StepAvailability.js
import React, { useState, useEffect, useMemo } from "react";
import SearchInputSection from "./SearchInputSection";
import { XCircle, CheckCircle } from "react-feather";
import Select from "react-select";
import axios from "axios";
import Swal from "sweetalert2";
import { toast } from "react-toastify";

// Component to handle room availability and booking with tax calculations
const StepAvailability = ({ handleNext, searchState, setSearchState }) => {
  // State for managing room data and booking selections
  const [filteredRooms, setFilteredRooms] = useState([]); // Available rooms from search
  const [selectedQuantity, setSelectedQuantity] = useState({}); // Quantity per room type
  const [billingSummary, setBillingSummary] = useState(null); // Summary of charges and taxes
  const [selectedRooms, setSelectedRooms] = useState([]); // User-selected rooms
  const [originalBillingSummary, setOriginalBillingSummary] = useState(null); // Original billing data
  const [flatRate, setFlatRate] = useState(null); // Custom flat rate override
  const [taxes, setTaxes] = useState([]); // Tax details from ns_tax_master
  const [taxRules, setTaxRules] = useState([]); // Tax allocation rules from ns_tax_allocation
  const [roomTypes, setRoomTypes] = useState([]); // Room types for validation
  const [roomSelections, setRoomSelections] = useState({
    isComplimentary: false, // Toggle for complimentary booking
    excludeTax: false, // Toggle to exclude taxes
    flatRate: null, // Custom flat rate
  });

  // Configuration from localStorage and environment
  const currency = localStorage.getItem("currencycode") || "INR"; // Currency code
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3000'; // API base URL
  const token = localStorage.getItem("token"); // Auth token
  const companyid = localStorage.getItem('companyid') || 61; // Company ID, fallback to 61
  const business_date = localStorage.getItem("business_date"); // Business date
  const parsedBusinessDate = business_date ? new Date(business_date) : null;
  const defaultCheckInDate =
    parsedBusinessDate && !isNaN(parsedBusinessDate.getTime())
      ? new Date(
          parsedBusinessDate.getFullYear(),
          parsedBusinessDate.getMonth(),
          parsedBusinessDate.getDate()
        )
      : new Date(); // Default check-in date

  // Destructure search state for booking parameters
  const { selectedSource, checkInDate, checkOutDate, rateCard, roomType } = searchState;

  // Meal plan options for room selection
  const mealPlanOptions = [
    { value: "EP", label: "EP" },
    { value: "CP", label: "CP" },
    { value: "MAP", label: "MAP" },
    { value: "AP", label: "AP" },
  ];

 const [isTaxDataLoading, setIsTaxDataLoading] = useState(true); 
  // Fetch tax allocation rules from /hmstax/tax_allocation/listing
useEffect(() => {
  const fetchTaxData = async () => {
    setIsTaxDataLoading(true);
    if (!companyid || !token) {
      toast.error("Company ID or token is missing.");
      setTaxRules([]);
      setTaxes([]);
      setIsTaxDataLoading(false);
      return;
    }
    try {
      const [taxRulesResponse, taxesResponse] = await Promise.all([
        axios.get(`${apiUrl}/hmstax/tax_allocation/listing`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { companyid },
        }),
        axios.get(`${apiUrl}/hmstax/listing`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { companyid },
        }),
      ]);
      if (taxRulesResponse.data.success && Array.isArray(taxRulesResponse.data.allocations)) {
        setTaxRules(taxRulesResponse.data.allocations);
      } else {
        toast.error("Failed to fetch tax rules.");
        setTaxRules([]);
      }
      if (taxesResponse.data.success && Array.isArray(taxesResponse.data.taxes)) {
        const currentDate = new Date();
        const applicableTaxes = taxesResponse.data.taxes.filter((tax) => {
          const startDate = new Date(tax.start_date);
          const endDate = tax.end_date ? new Date(tax.end_date) : null;
          return (
            tax.is_active &&
            tax.tax_type === "percentage" &&
            currentDate >= startDate &&
            (endDate === null || currentDate <= endDate)
          );
        }).map((tax) => ({
          tax_id: tax.tax_id,
          taxName: tax.tax_name,
          taxRate: parseFloat(tax.tax_rate),
          applicable_for: tax.applicable_for,
        }));
        const taxIds = taxRules.length > 0 ? [...new Set(taxRules.flatMap(allocation =>
          allocation.rules.flatMap(rule => rule.taxes)
        ))] : [];
        const filteredTaxes = applicableTaxes.filter(tax => taxIds.includes(tax.tax_id));
        setTaxes(filteredTaxes);
      } else {
        console.error("Failed to fetch taxes:", taxesResponse.data);
        setTaxes([]);
      }
    } catch (error) {
      console.error("Error fetching tax data:", error);
      toast.error("Error fetching tax data.");
      setTaxRules([]);
      setTaxes([]);
    } finally {
      setIsTaxDataLoading(false);
    }
  };
  fetchTaxData();
}, [companyid, token, apiUrl]);
  // Fetch room types from /roomtype/listings/
  useEffect(() => {
    const fetchRoomTypes = async () => {
      if (!companyid || !token) {
        toast.error('Company ID or token is missing.');
        setRoomTypes([]);
        return;
      }
      try {
        const response = await axios.get(`${apiUrl}/roomtype/listings/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        // Map room types to required format
        if (Array.isArray(response.data.roomTypes)) {
          setRoomTypes(response.data.roomTypes.map(type => ({
            room_type_id: type.room_type_id,
            type_name: type.type_name,
          })));
        } else {
          toast.error('Failed to fetch room types.');
          setRoomTypes([]);
        }
      } catch (error) {
        console.error('Error fetching room types:', error);
        toast.error('Error fetching room types.');
        setRoomTypes([]);
      }
    };
    fetchRoomTypes();
  }, [companyid, token, apiUrl]);

  // Fetch tax details from /hmstax/listing and filter by allocation rules
  useEffect(() => {
    const fetchTaxes = async () => {
      try {
        const response = await axios.get(`${apiUrl}/hmstax/listing`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { companyid },
        });

        if (response.data.success && Array.isArray(response.data.taxes)) {
          const currentDate = new Date();
          // Filter taxes by active status, percentage type, and date validity
          const applicableTaxes = response.data.taxes
            .filter((tax) => {
              const startDate = new Date(tax.start_date);
              const endDate = tax.end_date ? new Date(tax.end_date) : null;
              return (
                tax.is_active &&
                tax.tax_type === "percentage" &&
                currentDate >= startDate &&
                (endDate === null || currentDate <= endDate)
              );
            })
            .map((tax) => ({
              tax_id: tax.tax_id,
              taxName: tax.tax_name,
              taxRate: parseFloat(tax.tax_rate),
              applicable_for: tax.applicable_for,
            }));

          // Filter taxes by tax_id from allocation rules
          const taxIds = [...new Set(taxRules.flatMap(allocation =>
            allocation.rules.flatMap(rule => rule.taxes)
          ))];
          const filteredTaxes = applicableTaxes.filter(tax => taxIds.includes(tax.tax_id));
          setTaxes(filteredTaxes);
        } else {
          console.error("Failed to fetch taxes:", response.data);
          setTaxes([]);
        }
      } catch (error) {
        console.error("Error fetching taxes:", error);
        toast.error('Error fetching tax details.');
        setTaxes([]);
      }
    };

    fetchTaxes();
  }, [apiUrl, token, companyid, taxRules]);

  // Restore roomSelections from localStorage
  useEffect(() => {
    // Load saved room selections (e.g., complimentary, excludeTax, flatRate)
    const storedSelections = localStorage.getItem("roomSelections");
    if (storedSelections) {
      const parsedSelections = JSON.parse(storedSelections);
      setRoomSelections(parsedSelections);
      if (parsedSelections.flatRate !== undefined) {
        setFlatRate(parsedSelections.flatRate);
      }
    }
  }, []);

  // Restore selectedRooms, billingSummary, filteredRooms
  useEffect(() => {
    // Load saved selected rooms
    const storedSelectedRooms = localStorage.getItem("selectedRooms");
    if (storedSelectedRooms) {
      console.log("🔍 Loaded selectedRooms from storage:", JSON.parse(storedSelectedRooms));
      setSelectedRooms(JSON.parse(storedSelectedRooms));
    }

    // Load saved billing summary
    const storedBillingSummary = localStorage.getItem("billingSummary");
    if (storedBillingSummary) {
      const parsedSummary = JSON.parse(storedBillingSummary);
      console.log("🔍 Loaded billingSummary from storage:", parsedSummary);
      setBillingSummary(parsedSummary);
    }

    // Load saved filtered rooms
    const storedRooms = localStorage.getItem("filteredRooms");
    if (storedRooms) {
      console.log("🔍 Loaded filteredRooms from storage:", JSON.parse(storedRooms));
      setFilteredRooms(JSON.parse(storedRooms));
    }
  }, []);

  // Set default check-in and check-out dates
  useEffect(() => {
    // Set default check-in date if not provided
    if (
      !checkInDate ||
      new Date(checkInDate).toISOString().split("T")[0] ===
        new Date().toISOString().split("T")[0]
    ) {
      setSearchState((prev) => ({
        ...prev,
        checkInDate: defaultCheckInDate,
      }));
    }

    // Set default check-out date (one day after check-in)
    const defaultCheckOutDate = new Date(defaultCheckInDate);
    defaultCheckOutDate.setDate(defaultCheckOutDate.getDate() + 1);

    if (
      !checkOutDate ||
      new Date(checkOutDate).toISOString().split("T")[0] ===
        new Date(new Date().setDate(new Date().getDate() + 1))
          .toISOString()
          .split("T")[0]
    ) {
      setSearchState((prev) => ({
        ...prev,
        checkOutDate: defaultCheckOutDate,
      }));
    }

    // Update nights in search state
    if (searchState.checkInDate && searchState.checkOutDate) {
      const nights = calculateNights(searchState.checkInDate, searchState.checkOutDate);
      setSearchState((prev) => ({ ...prev, nights }));
    }
  }, [checkInDate, checkOutDate, setSearchState, defaultCheckInDate]);

  // Calculate the number of nights between check-in and check-out dates
  const calculateNights = (checkIn, checkOut) => {
    if (!checkIn || !checkOut) return 1;
    const inDate = new Date(checkIn);
    const outDate = new Date(checkOut);
    return Math.max(1, Math.ceil((outDate - inDate) / (1000 * 60 * 60 * 24)));
  };

  // Compute updated rooms with memoization for performance
  const updatedRooms = useMemo(() => {
    if (selectedRooms.length === 0) return [];
    const nights = calculateNights(checkInDate, checkOutDate);
    // Process each selected room to calculate charges and taxes
    return selectedRooms.map(room => {
      // Determine room rate based on flatRate or occupancy
      let roomRate;
      if (roomSelections.flatRate !== null && roomSelections.flatRate !== undefined) {
        roomRate = roomSelections.flatRate;
      } else {
        roomRate = (room.adults || 1) > 1 ? parseFloat(room.rate_double) : parseFloat(room.rate_single);
      }
      let roomCharges = (room.quantity || 1) * roomRate * nights;

      // Apply complimentary booking override
      if (roomSelections.isComplimentary) {
        roomCharges = 0;
      }

      // Filter applicable tax rules, prioritizing specific rules
      const applicableRules = taxRules
        .flatMap(allocation => allocation.rules)
        .filter(rule => {
          const roomMatch = rule.all_room_types
            ? !rule.excluded_room_types.includes(room.id) && rule.room_types.length === 0
            : rule.room_types.includes(room.id);
          const priceMatch = !rule.price_range ||
            (roomCharges >= rule.price_range.min_price && roomCharges <= rule.price_range.max_price);
          return roomMatch && priceMatch;
        })
        .sort((a, b) => {
          if (!a.all_room_types && b.all_room_types) return -1; // Prefer all_room_types: false
          if (a.all_room_types && !b.all_room_types) return 1;
          return b.room_types.length - a.room_types.length; // Prefer non-empty room_types
        });

      // Log rules for debugging
      console.log('Applicable rules for room:', room.id, applicableRules);

      // Get unique tax IDs from applicable rules
      const taxIds = [...new Set(applicableRules.flatMap(rule => rule.taxes))];
      console.log('Tax IDs for room:', room.id, taxIds);

      // Calculate tax details, respecting excludeTax and isComplimentary
      const roomTaxDetails = roomSelections.excludeTax || roomSelections.isComplimentary
        ? taxes.map(tax => ({
            taxName: tax.taxName,
            taxRate: tax.taxRate,
            taxAmount: 0,
          }))
        : taxes
            .filter(tax => taxIds.includes(tax.tax_id))
            .map(tax => ({
              taxName: tax.taxName,
              taxRate: tax.taxRate,
              taxAmount: (roomCharges * tax.taxRate) / 100,
            }));

      return {
        ...room,
        taxDetails: roomTaxDetails,
        appliedRate: {
          ...room.appliedRate,
          selected: roomRate,
        },
        excludeTax: roomSelections.excludeTax || false,
        isComplimentary: roomSelections.isComplimentary || false,
        roomCharges: roomCharges,
      };
    });
  }, [
    selectedRooms,
    checkInDate,
    checkOutDate,
    roomSelections.flatRate,
    roomSelections.excludeTax,
    roomSelections.isComplimentary,
    taxes,
    taxRules,
  ]);

  // Update billingSummary based on updatedRooms
useEffect(() => {
  if (updatedRooms.length > 0) {
    const nights = calculateNights(checkInDate, checkOutDate);
    let totalRoomCharges = 0;

    // Get all applicable tax IDs from updatedRooms
    const allTaxIds = [...new Set(updatedRooms.flatMap(room => 
      room.taxDetails.map(tax => taxes.find(t => t.taxName === tax.taxName)?.tax_id)
    ))];

    // Initialize totalTaxDetails with only the applicable taxes
    let totalTaxDetails = taxes
      .filter(tax => allTaxIds.includes(tax.tax_id))
      .map(tax => ({
        taxName: tax.taxName,
        taxRate: tax.taxRate,
        taxAmount: 0,
      }));

    updatedRooms.forEach((room) => {
      let roomRate = room.appliedRate?.selected || parseFloat(room.rate_single) || 0;
      const roomCharges = (room.quantity || 1) * roomRate * nights;

      if (roomSelections.isComplimentary) {
        totalRoomCharges = 0;
      } else {
        totalRoomCharges += roomCharges;
      }

      const roomTaxDetails = room.taxDetails || [];
      roomTaxDetails.forEach((tax) => {
        const taxIndex = totalTaxDetails.findIndex(t => t.taxName === tax.taxName);
        if (taxIndex !== -1) {
          totalTaxDetails[taxIndex].taxAmount += tax.taxAmount;
        }
      });
    });

    setBillingSummary((prev) => {
      const updatedSummary = {
        ...prev,
        roomCharges: Number(totalRoomCharges.toFixed(2)),
        taxDetails: totalTaxDetails,
        totalAmount: roomSelections.isComplimentary
          ? 0
          : roomSelections.excludeTax
            ? Number(totalRoomCharges.toFixed(2))
            : Number(
                (totalRoomCharges + totalTaxDetails.reduce((sum, tax) => sum + tax.taxAmount, 0)).toFixed(2)
              ),
        flatRate: roomSelections.flatRate,
        isComplimentary: roomSelections.isComplimentary,
        excludeTax: roomSelections.excludeTax,
        discountPercentage: 0,
      };
      localStorage.setItem("billingSummary", JSON.stringify(updatedSummary));
      return updatedSummary;
    });
  } else {
    setBillingSummary(null);
    localStorage.setItem("billingSummary", JSON.stringify(null));
  }
}, [updatedRooms, roomSelections.isComplimentary, roomSelections.excludeTax, taxes]);

  // Toggle handlers for Complimentary and Exclude Tax
  const handleToggle = (key, value) => {
    // Update room selections based on toggle
    setRoomSelections((prev) => {
      const updatedSelections = { ...prev, [key]: value };
      if (key === "isComplimentary" && value) {
        updatedSelections.excludeTax = false;
        updatedSelections.flatRate = null;
        setFlatRate(null);
      }
      localStorage.setItem("roomSelections", JSON.stringify(updatedSelections));
      return updatedSelections;
    });

    // Update selected rooms with new charges and taxes
    setSelectedRooms((prevRooms) => {
      if (prevRooms.length === 0) return prevRooms;
      const nights = calculateNights(checkInDate, checkOutDate);
      const updatedRooms = prevRooms.map(room => {
        let roomRate;
        if (roomSelections.flatRate !== null && roomSelections.flatRate !== undefined) {
          roomRate = roomSelections.flatRate;
        } else {
          roomRate = (room.adults || 1) > 1 ? parseFloat(room.rate_double) : parseFloat(room.rate_single);
        }
        let roomCharges = (room.quantity || 1) * roomRate * nights;

        if (key === "isComplimentary" && value) {
          roomCharges = 0;
        }

        // Filter applicable tax rules
        const applicableRules = taxRules
          .flatMap(allocation => allocation.rules)
          .filter(rule => {
            const roomMatch = rule.all_room_types
              ? !rule.excluded_room_types.includes(room.id) && rule.room_types.length === 0
              : rule.room_types.includes(room.id);
            const priceMatch = !rule.price_range ||
              (roomCharges >= rule.price_range.min_price && roomCharges <= rule.price_range.max_price);
            return roomMatch && priceMatch;
          })
          .sort((a, b) => {
            if (!a.all_room_types && b.all_room_types) return -1;
            if (a.all_room_types && !b.all_room_types) return 1;
            return b.room_types.length - a.room_types.length;
          });

        console.log('Applicable rules for room in handleToggle:', room.id, applicableRules);

        const taxIds = [...new Set(applicableRules.flatMap(rule => rule.taxes))];
        console.log('Tax IDs for room in handleToggle:', room.id, taxIds);

        const roomTaxDetails = (key === "excludeTax" && value) || (key === "isComplimentary" && value)
          ? taxes.map(tax => ({
              taxName: tax.taxName,
              taxRate: tax.taxRate,
              taxAmount: 0,
            }))
          : taxes
              .filter(tax => taxIds.includes(tax.tax_id))
              .map(tax => ({
                taxName: tax.taxName,
                taxRate: tax.taxRate,
                taxAmount: (roomCharges * tax.taxRate) / 100,
              }));

        return {
          ...room,
          taxDetails: roomTaxDetails,
          appliedRate: {
            ...room.appliedRate,
            selected: roomRate,
          },
          excludeTax: key === "excludeTax" ? value : roomSelections.excludeTax || false,
          isComplimentary: key === "isComplimentary" ? value : roomSelections.isComplimentary || false,
          roomCharges: roomCharges,
        };
      });

      // Check if rooms have changed to update LocalStorage
      const hasChanged = updatedRooms.some((room, index) => {
        const prevRoom = prevRooms[index];
        return (
          room.roomCharges !== prevRoom.roomCharges ||
          room.excludeTax !== prevRoom.excludeTax ||
          room.isComplimentary !== prevRoom.isComplimentary ||
          JSON.stringify(room.taxDetails) !== JSON.stringify(prevRoom.taxDetails)
        );
      });

      if (hasChanged) {
        localStorage.setItem("selectedRooms", JSON.stringify(updatedRooms));
        console.log("Updated selectedRooms in handleToggle:", updatedRooms);
        return updatedRooms;
      }
      return prevRooms;
    });

    // Update billing summary based on toggle
    setBillingSummary((prev) => {
      if (!prev) return prev;
      let updatedSummary = { ...prev, [key]: value };
      if (!prev.originalRates) {
        updatedSummary.originalRates = {
          roomCharges: prev.roomCharges,
          taxDetails: prev.taxDetails,
          totalAmount: prev.totalAmount,
          flatRate: prev.flatRate,
        };
      }

      if (key === "isComplimentary") {
        if (value) {
          updatedSummary = {
            ...updatedSummary,
            roomCharges: 0,
            taxDetails: taxes.map(tax => ({
              taxName: tax.taxName,
              taxRate: tax.taxRate,
              taxAmount: 0,
            })),
            totalAmount: 0,
            excludeTax: false,
            flatRate: null,
            discountPercentage: 0,
          };
        } else {
          updatedSummary = {
            ...updatedSummary,
            ...prev.originalRates,
            flatRate: prev.originalRates?.flatRate || null,
            discountPercentage: prev.originalRates?.discountPercentage || 0,
          };
        }
      }

      if (key === "excludeTax") {
        const baseAmount =
          updatedSummary.flatRate ??
          updatedSummary.originalRates?.roomCharges ??
          updatedSummary.roomCharges;
        if (value) {
          updatedSummary.taxDetails = taxes.map(tax => ({
            taxName: tax.taxName,
            taxRate: tax.taxRate,
            taxAmount: 0,
          }));
          updatedSummary.totalAmount = baseAmount;
        } else {
          const applicableRules = taxRules
            .flatMap(allocation => allocation.rules)
            .filter(rule => {
              const roomMatch = updatedRooms.some(room =>
                rule.all_room_types
                  ? !rule.excluded_room_types.includes(room.id) && rule.room_types.length === 0
                  : rule.room_types.includes(room.id)
              );
              const priceMatch = !rule.price_range ||
                (baseAmount >= rule.price_range.min_price && baseAmount <= rule.price_range.max_price);
              return roomMatch && priceMatch;
            })
            .sort((a, b) => {
              if (!a.all_room_types && b.all_room_types) return -1;
              if (a.all_room_types && !b.all_room_types) return 1;
              return b.room_types.length - a.room_types.length;
            });

          console.log('Applicable rules for billingSummary in handleToggle:', applicableRules);

          const taxIds = [...new Set(applicableRules.flatMap(rule => rule.taxes))];
          console.log('Tax IDs for billingSummary in handleToggle:', taxIds);

          updatedSummary.taxDetails = taxes
            .filter(tax => taxIds.includes(tax.tax_id))
            .map(tax => ({
              taxName: tax.taxName,
              taxRate: tax.taxRate,
              taxAmount: (baseAmount * tax.taxRate) / 100,
            }));
          updatedSummary.totalAmount = baseAmount + updatedSummary.taxDetails.reduce(
            (sum, tax) => sum + tax.taxAmount,
            0
          );
        }
      }

      localStorage.setItem("billingSummary", JSON.stringify(updatedSummary));
      return updatedSummary;
    });
  };

  // Handle Flat Rate changes
const handleFlatRateChange = (value) => {
  const rawFlatRate = value ? parseFloat(value) : null;
  const newFlatRate = (rawFlatRate === null || isNaN(rawFlatRate) || rawFlatRate <= 0) ? null : rawFlatRate;
  setFlatRate(newFlatRate);

  setRoomSelections((prev) => {
    const updatedSelections = { ...prev, flatRate: newFlatRate };
    localStorage.setItem("roomSelections", JSON.stringify(updatedSelections));
    return updatedSelections;
  });

  setSelectedRooms((prevRooms) => {
    if (prevRooms.length === 0) return prevRooms;
    const nights = calculateNights(checkInDate, checkOutDate);
    const updatedRooms = prevRooms.map(room => {
      let roomRate;
      if (newFlatRate !== null && newFlatRate !== undefined) {
        roomRate = newFlatRate;
      } else {
        roomRate = (room.adults || 1) > 1 ? parseFloat(room.rate_double) : parseFloat(room.rate_single);
      }
      const roomCharges = (room.quantity || 1) * roomRate * nights;

      const applicableRules = taxRules
        .flatMap(allocation => allocation.rules)
        .filter(rule => {
          const roomMatch = rule.all_room_types
            ? !rule.excluded_room_types.includes(room.id) && rule.room_types.length === 0
            : rule.room_types.includes(room.id);
          const priceMatch = !rule.price_range ||
            (roomCharges >= rule.price_range.min_price && roomCharges <= rule.price_range.max_price);
          return roomMatch && priceMatch;
        })
        .sort((a, b) => {
          const aSpecific = a.room_types.includes(room.id);
          const bSpecific = b.room_types.includes(room.id);
          if (aSpecific && !bSpecific) return -1;
          if (!aSpecific && bSpecific) return 1;
          if (!a.all_room_types && b.all_room_types) return -1;
          if (a.all_room_types && !b.all_room_types) return 1;
          return b.room_types.length - a.room_types.length;
        });

      console.log('Room Charges:', roomCharges);
      console.log('Applicable rules:', applicableRules.map(r => ({
        rule: r,
        room_types: r.room_types,
        price_range: r.price_range,
        taxes: r.taxes,
      })));
      const taxIds = [...new Set(applicableRules.slice(0, 1).flatMap(rule => rule.taxes))];
      console.log('Tax IDs:', taxIds);
      console.log('Selected Taxes:', taxes.filter(tax => taxIds.includes(tax.tax_id)));

      const roomTaxDetails = roomSelections.excludeTax || roomSelections.isComplimentary
        ? taxes.map(tax => ({
            taxName: tax.taxName,
            taxRate: tax.taxRate,
            taxAmount: 0,
          }))
        : (room.originalTaxDetails || taxes.filter(tax => taxIds.includes(tax.tax_id)))
            .map(tax => ({
              taxName: tax.taxName,
              taxRate: tax.taxRate,
              taxAmount: (roomCharges * tax.taxRate) / 100,
            }));

      return {
        ...room,
        taxDetails: roomTaxDetails,
        appliedRate: {
          ...room.appliedRate,
          selected: roomRate,
        },
        excludeTax: roomSelections.excludeTax || false,
        isComplimentary: roomSelections.isComplimentary || false,
        roomCharges: roomCharges,
      };
    });

    const hasChanged = updatedRooms.some((room, index) => {
      const prevRoom = prevRooms[index];
      return (
        room.appliedRate.selected !== prevRoom.appliedRate.selected ||
        room.roomCharges !== prevRoom.roomCharges ||
        JSON.stringify(room.taxDetails) !== JSON.stringify(prevRoom.taxDetails)
      );
    });

    if (hasChanged) {
      localStorage.setItem("selectedRooms", JSON.stringify(updatedRooms));
      console.log("Updated selectedRooms in handleFlatRateChange:", updatedRooms);
      return updatedRooms;
    }
    return prevRooms;
  });

  setBillingSummary((prev) => {
    if (!prev) return prev;
    let originalRoomCharges = prev.originalRates?.roomCharges ?? prev.roomCharges;
    let baseAmount = newFlatRate ?? originalRoomCharges;

    let discountPercentage = 0;
    if (newFlatRate !== null && newFlatRate !== undefined) {
      discountPercentage = ((originalRoomCharges - newFlatRate) / originalRoomCharges) * 100;
    }

    let updatedSummary = {
      ...prev,
      flatRate: newFlatRate,
      roomCharges: baseAmount,
      discountPercentage: discountPercentage,
      taxDetails: prev.excludeTax || prev.isComplimentary
        ? taxes.map(tax => ({
            taxName: tax.taxName,
            taxRate: tax.taxRate,
            taxAmount: 0,
          }))
        : updatedRooms.reduce((acc, room) => {
            room.taxDetails.forEach(tax => {
              const existing = acc.find(t => t.taxName === tax.taxName);
              if (existing) {
                existing.taxAmount += tax.taxAmount;
              } else {
                acc.push({ ...tax });
              }
            });
            return acc;
          }, []),
      totalAmount: prev.excludeTax || prev.isComplimentary
        ? baseAmount
        : baseAmount + updatedRooms.reduce((sum, room) =>
            sum + room.taxDetails.reduce((taxSum, tax) => taxSum + tax.taxAmount, 0), 0),
    };

    localStorage.setItem("billingSummary", JSON.stringify(updatedSummary));
    return updatedSummary;
  });
};

  // Update search state and save to localStorage
  const updateSearchState = (key, value) => {
    // Update search parameters and persist to LocalStorage
    setSearchState((prev) => {
      const updatedState = { ...prev, [key]: value };
      localStorage.setItem("searchState", JSON.stringify(updatedState));
      return updatedState;
    });
  };

  // Calculate total billing summary
  const calculateTotal = (summary) => {
    // Compute total charges with discounts and taxes
    let roomCharges =
      summary.flatRate !== null ? summary.flatRate : summary.roomCharges;
    let discountAmount = (roomCharges * (summary.discountPercentage || 0)) / 100;
    let discountedRoomCharges = roomCharges - discountAmount;

    // Filter applicable tax rules for the summary
    const applicableRules = taxRules
      .flatMap(allocation => allocation.rules)
      .filter(rule => {
        const roomMatch = updatedRooms.some(room =>
          rule.all_room_types
            ? !rule.excluded_room_types.includes(room.id) && rule.room_types.length === 0
            : rule.room_types.includes(room.id)
        );
        const priceMatch = !rule.price_range ||
          (discountedRoomCharges >= rule.price_range.min_price && discountedRoomCharges <= rule.price_range.max_price);
        return roomMatch && priceMatch;
      })
      .sort((a, b) => {
        if (!a.all_room_types && b.all_room_types) return -1;
        if (a.all_room_types && !b.all_room_types) return 1;
        return b.room_types.length - a.room_types.length;
      });

    console.log('Applicable rules in calculateTotal:', applicableRules);

    const taxIds = [...new Set(applicableRules.flatMap(rule => rule.taxes))];
    console.log('Tax IDs in calculateTotal:', taxIds);

    // Apply taxes, respecting excludeTax and isComplimentary
    let taxDetails = summary.excludeTax || summary.isComplimentary
      ? taxes.map(tax => ({
          taxName: tax.taxName,
          taxRate: tax.taxRate,
          taxAmount: 0,
        }))
      : taxes
          .filter(tax => taxIds.includes(tax.tax_id))
          .map(tax => ({
            taxName: tax.taxName,
            taxRate: tax.taxRate,
            taxAmount: (discountedRoomCharges * tax.taxRate) / 100,
          }));

    let totalAmount = discountedRoomCharges + taxDetails.reduce((sum, tax) => sum + tax.taxAmount, 0);

    return {
      ...summary,
      roomCharges: discountedRoomCharges,
      taxDetails,
      totalAmount,
    };
  };

  // Handle search results from SearchInputSection
  const handleSearchResults = (data, clearAll = false) => {
    // Clear all data if requested
    if (clearAll) {
      setFilteredRooms([]);
      setSelectedRooms([]);
      setBillingSummary(null);
      setFlatRate(null);
      setRoomSelections({
        isComplimentary: false,
        excludeTax: false,
        flatRate: null,
      });
      localStorage.removeItem("filteredRooms");
      localStorage.removeItem("selectedRooms");
      localStorage.removeItem("billingSummary");
      localStorage.removeItem("roomSelections");
      return;
    }
    // Process search results to update available rooms
    if (data && Array.isArray(data.roomTypes)) {
      const updatedRooms = data.roomTypes.map((room) => ({
        id: room.room_type_id,
        type: room.type_name,
        rate_single: room.rate_single,
        rate_double: room.rate_double,
        available: parseInt(room.available_rooms, 10),
        max_occupancy: parseInt(room.max_occupancy, 10),
      }));

      setFilteredRooms(updatedRooms);
      localStorage.setItem("filteredRooms", JSON.stringify(updatedRooms));
    } else {
      setFilteredRooms([]);
    }
  };

  // Handle quantity selection for rooms
  const handleQuantityChange = (roomId, value) => {
    // Update quantity for a room type
    setSelectedQuantity((prev) => ({ ...prev, [roomId]: value }));
  };

  // Handle changes to room selections
  const handleRoomSelectionChange = (roomId, key, value) => {
    // Update room-specific selections (quantity, adults, children, mealPlan)
    setRoomSelections((prev) => {
      const currentRoomSelection = prev[roomId] || {
        quantity: 1,
        adults: 1,
        children: 0,
        mealPlan: "EP",
      };
      const updatedSelections = {
        ...prev,
        [roomId]: {
          ...currentRoomSelection,
          [key]: value,
        },
      };
      localStorage.setItem("roomSelections", JSON.stringify(updatedSelections));
      return updatedSelections;
    });
  };

  // Handle adding a room with validation
  const handleAddRoom = (room) => {
    // Get room selection details
    const selection = roomSelections[room.id] || {
      quantity: 1,
      adults: 1,
      children: 0,
      mealPlan: "EP",
    };

    const updatedSelection = {
      ...selection,
      mealPlan: selection.mealPlan || "EP",
    };

    // Validate minimum quantity
    if (selection.quantity <= 0) {
      alert("Please select at least 1 room.");
      return;
    }

    // Prevent duplicate room additions
    if (selectedRooms.some((r) => r.id === room.id)) {
      alert("Room already added.");
      return;
    }

    // Validate maximum occupancy
    const totalGuests = (updatedSelection.adults || 0) + (updatedSelection.children || 0);
    const maxOccupancy = room.max_occupancy || 4;
    if (totalGuests > maxOccupancy) {
      return Swal.fire({
        title: "Warning",
        text: `You are exceeding max occupant (${maxOccupancy}) of Room. Proceed?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#28a745",
        cancelButtonColor: "#ff4d4f",
        confirmButtonText: "Yes",
        cancelButtonText: "No",
      }).then((result) => {
        if (result.isConfirmed) {
          proceedWithAddRoom(room, updatedSelection);
        }
      });
    }

    proceedWithAddRoom(room, updatedSelection);
  };

  // Proceed with adding the room
const proceedWithAddRoom = (room, selection) => {
  const nights = calculateNights(checkInDate, checkOutDate);
  const baseRate = (selection.adults || 1) > 1 ? parseFloat(room.rate_double) : parseFloat(room.rate_single);
  const roomRate = roomSelections.flatRate !== null && roomSelections.flatRate !== undefined
    ? roomSelections.flatRate
    : baseRate;
  const roomCharges = (selection.quantity || 1) * roomRate * nights;

  const applicableRules = taxRules
    .flatMap(allocation => allocation.rules)
    .filter(rule => {
      const roomMatch = rule.all_room_types
        ? !rule.excluded_room_types.includes(room.id) && rule.room_types.length === 0
        : rule.room_types.includes(room.id);
      const priceMatch = !rule.price_range ||
        (roomCharges >= rule.price_range.min_price && roomCharges <= rule.price_range.max_price);
      return roomMatch && priceMatch;
    })
    .sort((a, b) => {
      // Prioritize rules that explicitly include the room type
      const aSpecific = a.room_types.includes(room.id);
      const bSpecific = b.room_types.includes(room.id);
      if (aSpecific && !bSpecific) return -1;
      if (!aSpecific && bSpecific) return 1;
      // Then prioritize rules with all_room_types: false
      if (!a.all_room_types && b.all_room_types) return -1;
      if (a.all_room_types && !b.all_room_types) return 1;
      // Finally, prefer rules with more specific room_types
      return b.room_types.length - a.room_types.length;
    });

  console.log('Room Charges in proceedWithAddRoom:', roomCharges);
  console.log('Applicable rules in proceedWithAddRoom:', room.id, applicableRules.map(r => ({
    rule: r,
    room_types: r.room_types,
    price_range: r.price_range,
    taxes: r.taxes,
  })));

  // Take the most specific rule (first after sorting)
  const taxIds = [...new Set(applicableRules.slice(0, 1).flatMap(rule => rule.taxes))];
  console.log('Tax IDs in proceedWithAddRoom:', room.id, taxIds);

  // Map tax IDs to taxes and calculate tax amounts
  const roomTaxDetails = roomSelections.excludeTax || roomSelections.isComplimentary
    ? taxes.map(tax => ({
        taxName: tax.taxName,
        taxRate: tax.taxRate,
        taxAmount: 0,
      }))
    : taxes
        .filter(tax => taxIds.includes(tax.tax_id))
        .map(tax => {
          const taxAmount = (roomCharges * tax.taxRate) / 100;
          // Validate tax rate consistency
          const expectedTaxAmount = (roomCharges * 6) / 100; // Expected rate for CGST/SGST
          if (tax.taxRate !== 6 && taxAmount === expectedTaxAmount) {
            console.warn(`Tax rate mismatch for ${tax.taxName}: expected 6%, but tax rate is ${tax.taxRate}%`);
          }
          return {
            taxName: tax.taxName,
            taxRate: tax.taxRate,
            taxAmount: taxAmount,
          };
        });

  console.log('Room Tax Details in proceedWithAddRoom:', roomTaxDetails);

  const newRoom = {
    ...room,
    checkInDate,
    checkOutDate,
    quantity: selection.quantity,
    adults: selection.adults,
    children: selection.children,
    mealPlan: selection.mealPlan,
    originalRate: {
      single: parseFloat(room.rate_single),
      double: parseFloat(room.rate_double),
    },
    appliedRate: {
      single: parseFloat(room.rate_single),
      double: parseFloat(room.rate_double),
      selected: roomRate,
    },
    excludeTax: roomSelections.excludeTax || false,
    isComplimentary: roomSelections.isComplimentary || false,
    taxDetails: roomTaxDetails,
    originalTaxDetails: roomTaxDetails, // Store for later use
    roomCharges: roomCharges,
  };

  setSelectedRooms((prevRooms) => {
    const updatedRooms = [...prevRooms, newRoom];
    localStorage.setItem("selectedRooms", JSON.stringify(updatedRooms));
    return updatedRooms;
  });
};
  // Handle removing a room
  const handleRemoveRoom = (roomId) => {
    // Remove a room from selected rooms
    console.log("Removing room with ID:", roomId, "Current selectedRooms:", selectedRooms);
    setSelectedRooms((prevRooms) => {
      const updatedRooms = prevRooms.filter((room) => room.id !== parseInt(roomId));
      localStorage.setItem("selectedRooms", JSON.stringify(updatedRooms));
      console.log("After removing room:", updatedRooms);
      return updatedRooms;
    });
  };

  return (
    <div className="row form-bg">
      {/* Search input section for booking parameters */}
      <SearchInputSection
        checkInDate={checkInDate}
        setCheckInDate={(date) => updateSearchState("checkInDate", date)}
        checkOutDate={checkOutDate}
        setCheckOutDate={(date) => updateSearchState("checkOutDate", date)}
        selectedSource={selectedSource}
        setSelectedSource={(value) => updateSearchState("selectedSource", value)}
        rateCard={rateCard}
        setRateCard={(value) => updateSearchState("rateCard", value)}
        roomType={roomType}
        setRoomType={(value) => updateSearchState("roomType", value)}
        handleSearchResults={handleSearchResults}
        searchState={searchState}
        setSearchState={setSearchState}
        hasSelectedRooms={selectedRooms.length > 0}
      />

      {/* Left panel for selected rooms */}
      <div className="col-md-2 left-placeholder">
        <h4>Accommodation</h4>
        {selectedRooms.length > 0 ? (
          updatedRooms.map((room) => (
            <div
              key={room.id}
              className="selected-room-summary d-flex align-items-center justify-content-between"
            >
              <div>
                <p className="m-0">
                  <strong>{room.type}</strong>
                </p>
                <p className="text-muted">
                  {billingSummary?.isComplimentary ? (
                    "Complimentary"
                  ) : (
                    <>
                      Room Rent: {currency}{" "}
                      {(room.roomCharges || 0).toFixed(2)}
                      <br />
                      (Per Night: {currency}{" "}
                      {(room.appliedRate?.selected || room.rate_single || 0).toFixed(2)})
                    </>
                  )}
                </p>
              </div>
              <XCircle
                size={18}
                color="red"
                style={{ cursor: "pointer" }}
                onClick={() => handleRemoveRoom(room.id)}
                title="Remove selection"
              />
            </div>
          ))
        ) : (
          <p className="text-muted">
            Please select an accommodation to view its details here.
          </p>
        )}
      </div>

      {/* Right panel with room availability table */}
      <div className="col-md-10 right-table">
        <table className="availability-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>SOR</th>
              <th>DOR</th>
              <th>Available</th>
              <th>Quantity</th>
              <th>Adults</th>
              <th>Children</th>
              <th>Meal Plan</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredRooms.length > 0 ? (
              filteredRooms.map((room) => {
                const isAdded = selectedRooms.some((r) => r.id === room.id);
                return (
                  <tr key={room.id}>
                    <td>
                      <div className="d-flex align-items-center">
                        {room.type}
                        {isAdded && (
                          <CheckCircle
                            size={18}
                            color="green"
                            style={{ marginLeft: "8px" }}
                            title="Room Type Added"
                          />
                        )}
                      </div>
                    </td>
                    <td>{room.rate_single}</td>
                    <td>{room.rate_double}</td>
                    <td>{room.available}</td>
                    <td>
                      <select
                        className="quantity-select"
                        disabled={room.available === 0 || isAdded}
                        value={roomSelections[room.id]?.quantity || 1}
                        onChange={(e) =>
                          handleRoomSelectionChange(
                            room.id,
                            "quantity",
                            parseInt(e.target.value, 10)
                          )
                        }
                      >
                        {[...Array(room.available + 1).keys()]
                          .slice(1)
                          .map((qty) => (
                            <option key={qty} value={qty}>
                              {qty}
                            </option>
                          ))}
                      </select>
                    </td>
                    <td>
                      <input
                        type="number"
                        className="small-input"
                        min="1"
                        max="10"
                        value={roomSelections[room.id]?.adults || 1}
                        disabled={isAdded}
                        onChange={(e) =>
                          handleRoomSelectionChange(
                            room.id,
                            "adults",
                            parseInt(e.target.value, 10)
                          )
                        }
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        className="small-input"
                        min="0"
                        max="4"
                        value={roomSelections[room.id]?.children || 0}
                        disabled={isAdded}
                        onChange={(e) =>
                          handleRoomSelectionChange(
                            room.id,
                            "children",
                            parseInt(e.target.value, 10)
                          )
                        }
                      />
                    </td>
                    <td>
                      <Select
                        options={mealPlanOptions}
                        value={mealPlanOptions.find(
                          (option) =>
                            option.value ===
                            (roomSelections[room.id]?.mealPlan || "EP")
                        )}
                        onChange={(selectedOption) =>
                          handleRoomSelectionChange(
                            room.id,
                            "mealPlan",
                            selectedOption.value
                          )
                        }
                        isDisabled={room.available === 0 || isAdded}
                        styles={{
                          control: (base) => ({
                            ...base,
                            minWidth: "80px",
                          }),
                        }}
                      />
                    </td>
                    <td>
                      <button
                        className="btn btn-add"
                        disabled={room.available === 0 || isAdded}
                        onClick={() => handleAddRoom(room)}
                      >
                        {isAdded ? "Added" : "Add"}
                      </button>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="9" className="text-center">
                  <div className="no-data-container text-center mt-4">
                    <img
                      src="https://ui-cdn.digitalocean.com/apps/ui-spaces/static/media/spaces-empty.5e1d7947c43b7e336b14e421d4a86639.svg"
                      alt="No data available"
                      style={{ width: "150px", marginBottom: "20px" }}
                    />
                    <p>Please perform a search to find available rooms.</p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Billing summary section */}
      {billingSummary && (
        <div className="col-md-12 total-summary mt-4">
          <div className="summary-horizontal-container p-3 bg-light rounded">
            <div className="row">
              <div className="col-md-4">
                <div className="p-3 border rounded bg-white">
                  {/* Complimentary booking toggle */}
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <span className="fw-bold">Complimentary Booking</span>
                    <div className="form-check form-switch form-switch-md">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="complimentaryToggle"
                        checked={roomSelections.isComplimentary || false}
                        onChange={(e) =>
                          handleToggle("isComplimentary", e.target.checked)
                        }
                      />
                    </div>
                  </div>

                  {/* Exclude tax toggle */}
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <span className="fw-bold">Exclude Tax</span>
                    <div className="form-check form-switch form-switch-md">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="excludeTaxToggle"
                        checked={roomSelections.excludeTax || false}
                        disabled={roomSelections.isComplimentary}
                        onChange={(e) =>
                          handleToggle("excludeTax", e.target.checked)
                        }
                      />
                    </div>
                  </div>

                  {/* Flat rate input */}
                  <div className="input-group input-group-sm">
                    <span className="input-group-text fw-bold">Flat Rate</span>
                    <input
                      type="number"
                      className="form-control"
                      value={flatRate || ""}
                      disabled={roomSelections.isComplimentary}
                      min="1"
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === "" || parseFloat(value) >= 1) {
                          handleFlatRateChange(value);
                        }
                      }}
                    />
                    <span
                      className="clear-icon-link input-group-text p-0 bg-transparent border-0"
                      style={{ cursor: "pointer" }}
                      onClick={() => handleFlatRateChange(null)}
                    >
                      <XCircle size={18} color="#dc3545" />
                    </span>
                  </div>
                </div>
              </div>

              <div className="col-md-8">
                <div className="p-3 border rounded bg-white">
                  {/* Display room charges */}
                  <div className="d-flex justify-content-between border-bottom pb-2">
                    <span className="fw-bold text-secondary">Room Charges</span>
                    <span className="fw-bold">
                      {currency} {billingSummary?.roomCharges?.toFixed(2) || "0.00"}
                      {billingSummary?.flatRate !== null && billingSummary?.flatRate !== undefined && (
                        <span className="text-muted fs-6">
                          {" "}
                          (Flat Rate Applied: {currency} {(billingSummary.flatRate || 0).toFixed(2)})
                        </span>
                      )}
                    </span>
                  </div>

                  {/* Display only applicable taxes (taxAmount > 0) */}
                  {(!billingSummary?.excludeTax && !billingSummary?.isComplimentary) && billingSummary?.taxDetails?.filter(tax => tax.taxAmount > 0).map((tax, index) => (
                    <div
                      key={index}
                      className="d-flex justify-content-between border-bottom pb-2"
                    >
                      <span className="fw-bold text-secondary">
                        {tax.taxName} ({tax.taxRate}%)
                      </span>
                      <span className="fw-bold">
                        {currency} {tax.taxAmount.toFixed(2)}
                      </span>
                    </div>
                  ))}

                  {/* Display grand total */}
                  <div className="d-flex justify-content-between pt-2">
                    <span className="fw-bold fs-5 text-dark">Grand Total</span>
                    <span className="fw-bold fs-5 text-primary">
                      {currency} {billingSummary?.totalAmount?.toFixed(2) || "0.00"}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation buttons */}
          <div className="button-group mt-4 d-flex justify-content-end">
            <button className="btn btn-secondary custom-large-button mr-3">
              Back
            </button>
            <button
              className="btn btn-success custom-large-button"
              onClick={() => {
                if (!billingSummary) {
                  alert("Please select a room before proceeding.");
                  return;
                }
                handleNext(billingSummary, selectedRooms, {
                  selectedSource,
                  checkInDate,
                  checkOutDate,
                  rateCard,
                  roomType,
                  roomSelections,
                });

               //alert(JSON.stringify(selectedRooms)) 
              }}
            >
              Next Step
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default StepAvailability;